import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  Divider,
  Typography,
  Grid,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  useNavigate,
  useNavigationType,
  useSearchParams,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootReducerType, AppDispatch, RootState } from "@store/store";
import { paymentThunk, postPayloadBkpayThunk } from "@store/thunk/PaymentThunk";
import {
  FeesDetailsComponentThunk,
  FeesDetailsThunk,
  fetchApplicationDetails,
  getIsCheckApplicationSubmittedThunk,
} from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import { cleanDigitSectionValue } from "@mui/x-date-pickers/internals/hooks/useField/useField.utils";
import PaymentLoader from "../../../utils/PaymentLoader";
import { toast } from "react-toastify";
import { fetchApplicationDetailsRow } from "@modules/provisional-payment/redux/thunk/application-detailThunk";
import { app } from "@config/firebaseConfig";
import { submitRenewalRequestZeroFeeThunk } from "../redux/thunk/renewalThunk";

interface RenewalPaymentProps {
  isProcessing: boolean;
}

const RenewalPayment: React.FC<RenewalPaymentProps> = ({ isProcessing }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const [provisionalFee, setProvisionalFee] = React.useState<any>({});
  const [FeeDetailsComponent, setFeeDetailsComponent] = React.useState<any>({});
  const { applicationDetails } = useSelector(
    (state: RootState) => state.applicationDetails || {},
  );

  const { applicationDetailsROW } = useSelector(
    (state: RootState) => state.applicationDetailsROW || {},
  );

  const [searchParams] = useSearchParams();
  const access = searchParams.get("access");
  const row = searchParams.get("row");
  const applicationId = access || row;
  const [applicationDetail, setApplicationDetail] = useState<any>(null);

  // ✅ Success modal state
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [submittedApplicationCode, setSubmittedApplicationCode] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!applicationId) return;

    const blockedApplicationId = sessionStorage.getItem(
      "renewalBackBlockedAppId",
    );

    if (
      navigationType === "POP" &&
      blockedApplicationId === String(applicationId)
    ) {
      navigate("/application-dashboard", { replace: true });
    }
  }, [applicationId, navigate, navigationType]);

  useEffect(() => {
    if (!applicationId) return;

    const fetchData = async () => {
      try {
        let res;

        if (access) {
          res = await dispatch(
            fetchApplicationDetails(applicationId as any),
          ).unwrap();
        } else if (row) {
          res = await dispatch(
            fetchApplicationDetailsRow(applicationId as any),
          ).unwrap();
        }

        // ✅ STORE LOCALLY
        setApplicationDetail(
          res && typeof res === "object" && "data" in res ? res.data : res,
        );
      } catch (error) {
        console.error("Error fetching application:", error);
      }
    };

    fetchData();
  }, [applicationId, access, row, dispatch]);

  const { profile } = useSelector((state: RootReducerType) => state.profile);
  // ✅ feesDetails from Redux
  const { feesDetails, loading } = useSelector(
    (state: any) => state.feesDetails,
  );

  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const fetchFeesComponent = async () => {
    if (applicationId && row) {
      try {
        const feesComponentAmount = await dispatch(
          FeesDetailsComponentThunk({ applicationId }),
        ).unwrap();
        if (feesComponentAmount) {
          setFeeDetailsComponent(feesComponentAmount || {});
        }
      } catch (error) {
        console.error("Error fetching Fees Component details:", error);
      }
    }
  };

  // ✅ Fetch feesDetails on load
  useEffect(() => {
    if (applicationId) {
      dispatch(FeesDetailsThunk({ applicationId: applicationId }));
      row && fetchFeesComponent();
    }
  }, [applicationId, dispatch]);

  const handlePostBase64 = async () => {
    try {
      const fullName = profile?.name || "";
      const nameParts = fullName
        .trim()
        .split(" ")
        .filter((part) => part.length > 0);
      const firstName = nameParts?.length > 0 ? nameParts?.[0] : "";
      const lastName =
        nameParts?.length > 1 ? nameParts?.slice(1).join(" ") : "";
      let mobileNumber = profile?.mobile_number || "";
      if (mobileNumber.startsWith("+91")) {
        mobileNumber = mobileNumber.slice(3);
      }
      const bkPayload = {
        application_id: applicationId,
        FirstName: firstName || "",
        LastName: lastName || "",
        Address1: profile?.address || "",
        Address2: "",
        PostalCode: profile?.pincode || "",
        City: profile?.district || "",
        StateRegion: profile?.district || "",
        State: profile?.state || "",
        CountryCode: "INDIA",
        MobileNumber: mobileNumber,
        payment_stage: 4,
      };
      const getSignedBase64: any = await dispatch(
        postPayloadBkpayThunk(bkPayload),
      ).unwrap();
      if (getSignedBase64.success === false) {
        toast.error(
          getSignedBase64.message ||
            "Failed to initiate payment. Please try again.",
        );
        setShowLoader(false);
        return;
      }
      const form = document.createElement("form");
      form.method = "POST";
      form.action =
        process.env.REACT_APP_BHARATKOSH_URL || "https://bharatkosh.gov.in/";

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "bharrkkosh";
      input.value = getSignedBase64;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      setShowLoader(false);
      console.error("Error posting base64:", error);
    }
  };

  const handlePayment = async () => {
    if (!applicationId) return;
    if (feesDetails?.renewalFees === 0 || !feesDetails) {
      try {
        const applicationRes = access
          ? await dispatch(
              fetchApplicationDetails(applicationId as any),
            ).unwrap()
          : await dispatch(
              fetchApplicationDetailsRow(applicationId as any),
            ).unwrap();

        const applicationSubmitted = await dispatch(
          submitRenewalRequestZeroFeeThunk(applicationId as any),
        ).unwrap();

        sessionStorage.setItem(
          "renewalBackBlockedAppId",
          String(applicationId),
        );

        // ✅ Open success modal instead of immediate navigation
        setSuccessMessage(
          applicationSubmitted?.message ||
            "Renewal request submitted successfully",
        );
        setSubmittedApplicationCode(applicationRes?.application_code || null);
        setSuccessModalOpen(true);
      } catch (error) {
        console.error("Error in payment done thunk:", error);
      }
    } else {
      setShowLoader(true);
      await handlePostBase64();
    }
    return;
  };

  // ✅ Success modal close handler → triggers navigation
  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
    if (submittedApplicationCode) {
      navigate(
        `/application-submitted?id=${submittedApplicationCode}`,
        { replace: true },
      );
      setSubmittedApplicationCode(null);
    }
  };

  useEffect(() => {
    if (isProcessing) {
      const timer = setTimeout(() => {
        setPaymentSuccess(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isProcessing]);

  useEffect(() => {
    if (paymentSuccess) {
      const redirectTimer = setTimeout(() => {
        navigate("/application-summary");
      }, 3000);
      return () => clearTimeout(redirectTimer);
    }
  }, [paymentSuccess, navigate]);

  return (
    <>
      <PaymentLoader open={showLoader} />
      <Box sx={{ py: 1, minHeight: "80vh", width: "82%", margin: "0 auto" }}>
        <Typography
          variant="h6"
          fontWeight={700}
          gutterBottom
          sx={{ fontSize: "20px" }}
        >
          Renewal Payment
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {applicationDetail?.utility_type_id != 5 ? (
          <>
            <Alert severity="error" sx={{ mb: 2, fontSize: "14px" }}>
              <strong>Important:</strong> Please wait for 20 minutes if you have
              recently attempted the payment. If the amount has been deducted
              but an Application was not requested for renewal, contact our
              support team at{" "}
              <a
                href="mailto: support-rmp@gov.in"
                style={{ textDecoration: "underline" }}
              >
                {" "}
                support-rmp@gov.in
              </a>{" "}
              for assistance.
            </Alert>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {/* Further Instructions Section */}
              <Box
                sx={{
                  flex: 1,
                  p: 3,
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  minWidth: 300,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  fontSize="18px"
                  color="text.primary"
                  gutterBottom
                  mb={2}
                >
                  Further Instructions
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "14px" }} paragraph>
                  1. In case of rejection of the Renewal Application, the
                  Applicant shall be required to pay a fresh, Non Refundable
                  Renewal NOC Fee amount applicable as per pervailing guideline
                  for re-submission.
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "14px" }} paragraph>
                  2. The processing, examination, and approval of the Renewal
                  Application shall be strictly governed by the prevailing
                  guidelines and policies issued by the Ministry of Road
                  Transport & Highways (MoRTH).
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "14px" }} paragraph>
                  3. The review process shall commence only after successful
                  submission of the application along with payment confirmation.
                </Typography>
              </Box>

              {/* Payment Details Section */}
              <Box sx={{ maxWidth: "40%" }}>
                {row && (
                  <Box
                    sx={{
                      p: 2,
                      border: "1px dashed #ff8b7a",
                      borderRadius: 2,
                      background: "#fff7f5",
                      mb: 3,
                    }}
                  >
                    <Typography fontWeight={600} fontSize={14} mb={1}>
                      NOC Fee Calculation:
                    </Typography>

                    {provisionalFee?.provisionalFees && (
                      <Typography fontSize={13} mb={1}>
                        A provisional NOC fee of amount ₹
                        {provisionalFee?.provisionalFees?.toLocaleString() || 0}{" "}
                        shall be added to the applicable charges to proceed
                        further.
                      </Typography>
                    )}

                    <Typography fontSize={13}>
                      Renewal Fee Amount = Utilized NH land area × Prevailing
                      Circle Rate of land per unit area{" "}
                      {applicationDetail?.utility_type === "Public Utility"
                        ? "of land per unit area × 1.5% per annum , subject to minimum of Rs. 10,000/-, with 6% annual increment. × 5 years"
                        : "× 10% × 5 years"}
                    </Typography>

                    {applicationDetail?.utility_type === "Public Utility" &&
                      feesDetails?.renewalFees === 0 && (
                        <Typography fontSize={12} color="success.main" mt={0.5}>
                          Renewal Fee amount will be waived off in this case.
                        </Typography>
                      )}

                    <Typography fontSize={12} color="text.secondary" mt={0.5}>
                      <b>Note</b>: The fee will be calculated for five (5) years
                      in advance.
                    </Typography>

                    <Box sx={{ borderBottom: "1px dotted #ff8b7a", my: 1.5 }} />

                    {FeeDetailsComponent?.finalFeesComponents ? (
                      <Typography fontSize={13}>
                        Renewal NOC Amount ={" "}
                        {FeeDetailsComponent?.finalFeesComponents
                          ?.utilizedNH_land_area || 0}{" "}
                        m × Rs{" "}
                        {FeeDetailsComponent?.finalFeesComponents?.circleRate ||
                          0}{" "}
                        {applicationDetail?.utility_type === "Public Utility"
                          ? "× 1.5% per annum, subject to minimum of Rs. 10,000/-, with 6% annual increment. × 5  = "
                          : "× 10% × 5 = "}
                        <b>
                          Rs.{" "}
                          {FeeDetailsComponent?.finalFeesComponents?.finalLicenseFees?.toLocaleString() ||
                            0}
                        </b>
                      </Typography>
                    ) : (
                      <Typography fontSize={13} color="text.secondary">
                        Loading Final NOC Fee details...
                      </Typography>
                    )}
                  </Box>
                )}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    mb={2}
                    color="#344054"
                    fontSize="18px"
                  >
                    Payment Details
                  </Typography>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" mb={1}>
                      <strong>Applicant Name:</strong> {profile?.name}
                    </Typography>
                    <Typography variant="body2" mb={1}>
                      <strong>Amenity Name:</strong>{" "}
                      {applicationDetail?.application_category ||
                        applicationDetail?.amenity ||
                        "-"}
                    </Typography>
                    {access && (
                      <Typography variant="body2" mb={1}>
                        <strong>Stretch Reach Type:</strong>{" "}
                        {applicationDetail?.location_type_name ||
                          applicationDetail?.location_type_name ||
                          "-"}
                      </Typography>
                    )}
                    {/* <Typography variant="body2" mb={1}>
                            <strong>Location Address:</strong> {address_1 || "14, noida sector 16, UP"}
                        </Typography> */}
                    <Typography variant="body2" mb={1}>
                      <strong>Location Address:</strong>{" "}
                      {[
                        applicationDetail?.address_1 ||
                          applicationDetail?.address1,
                        applicationDetail?.address_2 ||
                          applicationDetail?.address2,
                        applicationDetail?.district,
                        applicationDetail?.state,
                      ]
                        .filter(Boolean) // remove null/undefined/empty
                        .join(", ")}
                    </Typography>
                    <Typography variant="body2" mt={2}>
                      Fee Breakup for Renewal NOC
                    </Typography>
                  </Box>

                  <Box sx={{ borderBottom: "1px dashed #bbb", mb: 2 }} />

                  {/* Application Processing Fee */}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" sx={{ color: "#212121" }}>
                      Renewal Fee
                    </Typography>
                    <Typography variant="body2">
                      Rs.{" "}
                      {feesDetails?.renewalFees?.toLocaleString("en-IN") ||
                        "0.00"}
                    </Typography>
                  </Box>

                  {/* Conditionally render Penalty Fee */}
                  {feesDetails?.penaltyFees ? (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Penalty Fee</Typography>
                      <Typography variant="body2">
                        Rs. {feesDetails.penaltyFees.toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                  ) : null}

                  <Box sx={{ borderBottom: "1px dashed #bbb", my: 2 }} />

                  {/* Total calculation */}
                  <Box display="flex" justifyContent="space-between" mb={3}>
                    <Typography variant="body1" fontWeight={700}>
                      Total
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      Rs.{" "}
                      {feesDetails?.penaltyFees
                        ? (
                            (feesDetails?.renewalFees || 0) +
                            (feesDetails?.penaltyFees || 0)
                          ).toLocaleString("en-IN")
                        : feesDetails?.renewalFees?.toLocaleString("en-IN") ||
                          "0.00"}
                    </Typography>
                  </Box>

                  {feesDetails?.renewalFees === 0 || !feesDetails ? (
                    <>
                      <Typography
                        sx={{
                          bgcolor: "#f7e9e8",
                          color: "#8F1E17",
                          px: 2,
                          py: 1,
                          mt: 2,
                          textWrap: "wrap",
                        }}
                        fontSize={13}
                        mb={1}
                      >
                        <strong>Important Notice:</strong> In accordance with
                        the applicable guidelines, the total fee shall be waived
                        for this specific case.
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handlePayment}
                        sx={{
                          borderRadius: "24px",
                          textTransform: "none",
                          fontWeight: 600,
                          backgroundColor: "#3A1C73",
                          padding: "12px",
                          fontSize: "16px",
                          height: "40px",
                          "&:hover": {
                            backgroundColor: "#2a145c",
                          },
                        }}
                      >
                        Submit Application
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handlePayment}
                      disabled={showLoader}
                      sx={{
                        borderRadius: "24px",
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "#3A1C73",
                        padding: "12px",
                        fontSize: "16px",
                        height: "40px",
                        "&:hover": {
                          backgroundColor: "#2a145c",
                        },
                      }}
                    >
                      Pay Now
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={12}>
              <Card
                sx={{
                  // background: "#fefcfcff",
                  borderRadius: "16px",
                  px: { xs: 1, sm: 2 },
                  py: { xs: 2, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                }}
                variant="outlined"
              >
                <Box display="flex" alignItems="start" gap={1} mb={3}>
                  <InfoOutlinedIcon sx={{ color: "red", fontSize: 24 }} />
                  <Box>
                    <h3 className="font-medium text-justify brand-900">
                      <u>
                        <strong className="gray-900">Please Note:</strong>
                      </u>{" "}
                      In accordance with the Ministry of Road Transport and
                      Highways (MoRTH) Policy Guidelines No.{" "}
                      <strong>145.33</strong> dated <strong>15.11.2025</strong>,
                      the provisions related to the following components have
                      been waived for all BharatNet Applications submitted
                      through the Rajmarg Pravesh Portal:
                    </h3>
                    <ul style={{ marginLeft: "20px", marginTop: "8px" }}>
                      <li className="gray-900 font-medium text-justify mb-2">
                        <h3 className="brand-900"> Circle Rate Calculation</h3>
                      </li>
                      <li className="gray-900 font-medium text-justify mb-2">
                        <h3 className="brand-900">License Fee Calculation</h3>
                      </li>
                      <li className="gray-900 font-medium text-justify mb-2">
                        <h3 className="brand-900">
                          Performance Bank Guarantee (PBG) Calculation
                        </h3>
                      </li>
                    </ul>
                  </Box>
                </Box>
                <Box sx={{ marginLeft: "auto" }}>
                  <Button
                    variant="contained"
                    onClick={handlePayment}
                    sx={{
                      borderRadius: "24px",
                      textTransform: "none",
                      fontWeight: 600,
                      backgroundColor: "#3A1C73",
                      padding: "12px",
                      fontSize: "16px",
                      height: "40px",
                      textAlign: "right",
                      display: "flex",
                      justifyContent: "end",
                      "&:hover": {
                        backgroundColor: "#2a145c",
                      },
                    }}
                  >
                    Submit Application
                  </Button>
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* ✅ Success Modal */}
      <Dialog
        open={successModalOpen}
        onClose={handleSuccessModalClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogContent sx={{ textAlign: "center" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "center", mb: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "#2E7D32" }} />
            <Typography fontWeight={700} fontSize={18}>
              Success!
            </Typography>
          </Box>
          <Typography fontSize={14} color="text.secondary" mb={3}>
            {successMessage || "Renewal request submitted successfully."}
          </Typography>
          <Button
            variant="contained"
            onClick={handleSuccessModalClose}
            sx={{
              borderRadius: "24px",
              background: "#3A1C73",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              "&:hover": { background: "#2a145c" },
            }}
          >
            Continue
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RenewalPayment;